import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";

const TYPE_OPTIONS = [
  { value: "brand", label: "Company / Brand", icon: "fa-building", sub: "Manufacturer or retailer" },
  { value: "enforcement", label: "Enforcement", icon: "fa-shield-alt", sub: "Law enforcement agency" },
  { value: "judiciary", label: "Judiciary", icon: "fa-balance-scale", sub: "Court or legal body" },
];

const FieldLabel = ({ children, required }) => (
  <div
    style={{
      color: "#6b7280",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      marginBottom: 8,
    }}
  >
    {children}
    {required && <span style={{ color: "#ef4444", marginLeft: 4 }}>*</span>}
  </div>
);

const inputStyle = {
  width: "100%",
  backgroundColor: "#fff",
  border: "1.5px solid #e5e7eb",
  borderRadius: 10,
  padding: "10px 14px",
  fontSize: 14,
  color: "#111827",
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
};

const focusHandlers = {
  onFocus: (e) => {
    e.target.style.borderColor = "#ff5100";
    e.target.style.boxShadow = "0 0 0 3px rgba(255,81,0,0.1)";
  },
  onBlur: (e) => {
    e.target.style.borderColor = "#e5e7eb";
    e.target.style.boxShadow = "none";
  },
};

const SectionDivider = ({ label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 0 16px" }}>
    <div style={{ flex: 1, height: 1, background: "#f3f4f6" }} />
    <span style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em", textTransform: "uppercase" }}>
      {label}
    </span>
    <div style={{ flex: 1, height: 1, background: "#f3f4f6" }} />
  </div>
);

const CreateClient = ({ domain, show, handleClose, handleFormSubmit }) => {
  const [orgName, setOrgName] = useState("");
  const [typeValue, setTypeValue] = useState("");
  const [address, setAddress] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [primaryPhone, setPrimaryPhone] = useState("");
  const [secondaryPhone, setSecondaryPhone] = useState("");
  const [saving, setSaving] = useState(false);

  const resetState = () => {
    setOrgName("");
    setTypeValue("");
    setAddress("");
    setContactEmail("");
    setPrimaryPhone("");
    setSecondaryPhone("");
    setSaving(false);
  };

  const handleModalClose = () => {
    resetState();
    handleClose();
  };

  const submitForm = async () => {
    if (!orgName.trim() || !typeValue) {
      alert("Please fill in all required fields.");
      return;
    }

    const data = {
      organization: orgName.trim(),
      type: typeValue,
      ...(address.trim() && { address: address.trim() }),
      ...(contactEmail.trim() && { contactEmail: contactEmail.trim().toLowerCase() }),
      ...(primaryPhone.trim() && { primaryPhone: primaryPhone.trim() }),
      ...(secondaryPhone.trim() && { secondaryPhone: secondaryPhone.trim() }),
    };

    setSaving(true);
    try {
      const res = await fetch("/api/organizations/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Failed to create client");
      }
      resetState();
      handleClose();
      handleFormSubmit();
    } catch (error) {
      console.error("Error:", error);
      alert("Error creating client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <style>{`
        .create-client-modal .modal-content {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.12);
        }
        .create-client-modal .modal-header {
          background: #fff;
          border-bottom: 1px solid #f3f4f6;
          border-radius: 18px 18px 0 0;
          padding: 20px 24px 16px;
        }
        .create-client-modal .modal-body {
          background: #fff;
          padding: 20px 24px;
        }
        .create-client-modal .modal-footer {
          background: #f9fafb;
          border-top: 1px solid #f3f4f6;
          border-radius: 0 0 18px 18px;
          padding: 16px 24px;
        }
      `}</style>

      <Modal
        show={show}
        onHide={handleModalClose}
        dialogClassName="create-client-modal"
        centered
        style={{ backdropFilter: "blur(3px)" }}
      >
        <Modal.Header closeButton>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34, height: 34, borderRadius: 9,
                background: "rgba(255,81,0,0.08)",
                border: "1.5px solid rgba(255,81,0,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <i className="fas fa-handshake" style={{ color: "#ff5100", fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#111827" }}>Create Client</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>
                Add a new client to the platform
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div>
            <FieldLabel required>Client Name</FieldLabel>
            <input
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="e.g. Acme Uganda Ltd"
              disabled={saving}
              style={{ ...inputStyle, opacity: saving ? 0.6 : 1 }}
              {...focusHandlers}
            />
          </div>

          {/* Client type */}
          <div style={{ marginTop: 16 }}>
            <FieldLabel required>Client Type</FieldLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {TYPE_OPTIONS.map((opt) => {
                const selected = typeValue === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => !saving && setTypeValue(opt.value)}
                    disabled={saving}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 14px",
                      borderRadius: 10,
                      border: `1.5px solid ${selected ? "#ff5100" : "#e5e7eb"}`,
                      background: selected ? "rgba(255,81,0,0.06)" : "#fff",
                      cursor: saving ? "not-allowed" : "pointer",
                      textAlign: "left",
                      transition: "border-color 0.15s, background 0.15s",
                      opacity: saving ? 0.6 : 1,
                    }}
                  >
                    <div
                      style={{
                        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                        background: selected ? "rgba(255,81,0,0.12)" : "#f3f4f6",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      <i
                        className={`fas ${opt.icon}`}
                        style={{ fontSize: 13, color: selected ? "#ff5100" : "#9ca3af" }}
                      />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: selected ? "#ff5100" : "#111827" }}>
                        {opt.label}
                      </div>
                      <div style={{ fontSize: 11, color: selected ? "#f97316" : "#9ca3af", marginTop: 1 }}>
                        {opt.sub}
                      </div>
                    </div>
                    {selected && (
                      <i className="fas fa-check-circle" style={{ marginLeft: "auto", color: "#ff5100", fontSize: 16 }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Address */}
          <div style={{ marginTop: 16 }}>
            <FieldLabel>Address</FieldLabel>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="e.g. Plot 12, Kampala Road"
              disabled={saving}
              style={{ ...inputStyle, opacity: saving ? 0.6 : 1 }}
              {...focusHandlers}
            />
          </div>

          <SectionDivider label="Contact Info" />

          {/* Email */}
          <div>
            <FieldLabel>Contact Email</FieldLabel>
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="contact@client.com"
              disabled={saving}
              style={{ ...inputStyle, opacity: saving ? 0.6 : 1 }}
              {...focusHandlers}
            />
          </div>

          {/* Phone numbers side by side */}
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <div style={{ flex: 1 }}>
              <FieldLabel>Primary Phone</FieldLabel>
              <input
                type="tel"
                value={primaryPhone}
                onChange={(e) => setPrimaryPhone(e.target.value)}
                placeholder="+256 700 000 000"
                disabled={saving}
                style={{ ...inputStyle, opacity: saving ? 0.6 : 1 }}
                {...focusHandlers}
              />
            </div>
            <div style={{ flex: 1 }}>
              <FieldLabel>Secondary Phone</FieldLabel>
              <input
                type="tel"
                value={secondaryPhone}
                onChange={(e) => setSecondaryPhone(e.target.value)}
                placeholder="+256 700 000 000"
                disabled={saving}
                style={{ ...inputStyle, opacity: saving ? 0.6 : 1 }}
                {...focusHandlers}
              />
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            onClick={submitForm}
            disabled={saving}
            style={{
              width: "100%",
              padding: "11px 0",
              borderRadius: 10,
              border: "none",
              background: saving ? "#e5e7eb" : "linear-gradient(135deg, #c13d00 0%, #ff5100 100%)",
              color: saving ? "#9ca3af" : "#fff",
              fontWeight: 700,
              fontSize: 15,
              cursor: saving ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 9,
              transition: "background 0.15s",
            }}
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: 13 }} />
                Saving…
              </>
            ) : (
              <>
                Create Client
                <i className="fas fa-arrow-right" style={{ fontSize: 13 }} />
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateClient;
